import { useState } from "react"
import { NavLink, Stack, Text, Collapse, Box } from "@mantine/core"

interface CategoryNode {
  id: number | null
  name: string
  children: CategoryNode[]
}

interface Props {
  categories: { id: number; name: string }[]
  activeCategoryId: number | null
  onCategoryChange: (id: number | null) => void
}

function buildTree(flat: { id: number; name: string }[]): CategoryNode[] {
  const roots: CategoryNode[] = []
  const stack: CategoryNode[] = [{ id: null, name: "", children: roots }]

  for (const item of flat) {
    const prefixMatch = item.name.match(/^(--\s)*/)
    const depth = prefixMatch ? prefixMatch[0].length / 3 : 0
    const clean = item.name.replace(/^--\s*/g, "")
    const node: CategoryNode = { id: item.id, name: clean, children: [] }

    stack[depth + 1] = node
    const parent = stack[depth]
    if (parent) parent.children.push(node)
  }

  return roots
}

function CategoryNodeItem({
  node,
  activeId,
  onSelect,
}: {
  node: CategoryNode
  activeId: number | null
  onSelect: (id: number | null) => void
}) {
  const hasChildren = node.children.length > 0
  const [opened, setOpened] = useState(true)

  return (
    <Box>
      <NavLink
        label={node.name}
        active={node.id === activeId}
        onClick={() => onSelect(node.id)}
        rightSection={
          hasChildren ? (
            <i
              className={`fa fa-chevron-${opened ? "down" : "right"}`}
              onClick={(e) => {
                e.stopPropagation()
                setOpened((o) => !o)
              }}
              style={{ fontSize: 10, cursor: "pointer" }}
            />
          ) : null
        }
      />
      {hasChildren && (
        <Collapse in={opened}>
          {node.children.map((child) => (
            <Box key={child.id ?? "all"} pl={16}>
              <CategoryNodeItem node={child} activeId={activeId} onSelect={onSelect} />
            </Box>
          ))}
        </Collapse>
      )}
    </Box>
  )
}

export default function DocumentCategorySidebar({
  categories,
  activeCategoryId,
  onCategoryChange,
}: Props) {
  const tree = buildTree(categories)

  return (
    <Stack gap={0}>
      <Text size="xs" tt="uppercase" fw={600} c="dimmed" px="sm" pb="xs" lts="0.05em">
        Categorias
      </Text>
      <NavLink
        label="Todos os Documentos"
        active={activeCategoryId === null}
        onClick={() => onCategoryChange(null)}
        c="dimmed"
      />
      {tree.map((node) => (
        <CategoryNodeItem key={node.id ?? "all"} node={node} activeId={activeCategoryId} onSelect={onCategoryChange} />
      ))}
    </Stack>
  )
}
